import { Card, CardContent, CardMedia, Grid, useMediaQuery, useTheme } from '@mui/material';
import React, { useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { includes } from 'lodash';
import { OrderPageData, VoucherSoldTypes, VoucherType } from '../api';
import { OrderPageTypography } from './themedComponents/OrderPageTypography';
import { quotePrice, withBookingFee } from './core/pricing';
import { useLanguage } from '../utils/GeneralUtils';
import MonkeybookWidgetFooter from '../MonkeybookWidgetFooter';
import { useAccountData } from './components/AccountSpecificThemeWrapper';
import { customerFacingPriceFormatter } from './utils';
import { getOrderPages } from '../utils/AccountUtils';
import { parseArrayFromQueryString } from '../utils/orderPageQSSelectorUtil';
import { getPrimaryPhoto } from '../utils/orderPageImageUtils';
import { AccountLogo } from '../shared/ui/AccountLogo';

export const VoucherSelectionWidget = () => {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const language = useLanguage();
  const { t } = useTranslation();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const account = useAccountData();
  const queryParams = new URLSearchParams(location.search);
  const iframeHeight = document.body.offsetHeight;
  const iframeWidth = document.body.offsetWidth;

  window.parent.postMessage({ height: iframeHeight, width: iframeWidth }, '*');
  const elementRef = useCallback((node: any) => {
    if (!node) return;
    const resizeObserver = new ResizeObserver(() => {
      const iframeHeight = document.body.offsetHeight;
      window.parent.postMessage({ height: iframeHeight }, '*');
    });
    resizeObserver.observe(node);
  }, []);

  const embeddedWidget = queryParams.get('embed') === 'true';
  const initialOrderPageIds = parseArrayFromQueryString(queryParams, 'orderpages');

  const orderPages =
    initialOrderPageIds.length > 0
      ? getOrderPages(account).filter((o: OrderPageData) => includes(initialOrderPageIds, o.id))
      : getOrderPages(account);

  const voucherTiles = orderPages.flatMap((orderPage: OrderPageData) => {
    const soldTypes = orderPage.effective_vouchers_config?.sold_types ?? VoucherSoldTypes.EXPERIENCE;
    if (soldTypes === VoucherSoldTypes.NONE) {
      return [];
    }
    const voucherType = soldTypes === VoucherSoldTypes.MONETARY ? VoucherType.MONETARY : VoucherType.EXPERIENCE;
    return [{ orderPage, voucherType }];
  });

  const handleVoucherTileClick = (orderPage: OrderPageData, voucherType: VoucherType) => {
    const typeQuery = voucherType === VoucherType.MONETARY ? `&type=${VoucherType.MONETARY}` : '';
    navigate(`/order/${accountId}/${account?.experiences[0].id}/${orderPage.id}?vouchers=true${typeQuery}`);
  };

  return (
    <Grid>
      <Grid
        mb={2}
        container
        display="flex"
        justifyContent="space-around"
        sx={{ '::-webkit-scrollbar': { display: 'none' } }}
        style={{
          height: '100%',
          width: '100%',
        }}>
        <Grid
          ref={elementRef}
          container
          flexDirection="column"
          style={{
            borderRadius: '12px',
            background: theme.customTheme.palette.background,
            height: '100%',
            width: '100%',
            maxWidth: '710px',
          }}>
          <Grid
            px={isSmallScreen ? undefined : 4}
            container
            flexDirection="column"
            sx={{
              color: theme.customTheme.palette.text.primary,
              border: `2px solid ${theme.customTheme.palette.border}`,
              borderRadius: '12px',
            }}
            boxShadow={4}>
            <Grid container mt={isSmallScreen ? 0 : 3} mb={1} p={4} pt={0} flexDirection="column" alignContent="center">
              {!isSmallScreen && !embeddedWidget && (
                <Grid container justifyContent="center" mb={4}>
                  <AccountLogo account={account} width={250} style={{ width: 250 }} alt="logo" />
                </Grid>
              )}
              <Grid container mb={2} justifyContent="center">
                <OrderPageTypography variant="h1" bold>
                  {t('multiplVouchers.title')}
                </OrderPageTypography>
              </Grid>
              <Grid container gap={4} justifyContent="center">
                {voucherTiles.map(({ orderPage, voucherType }) => {
                  const isMonetary = voucherType === VoucherType.MONETARY;
                  const { total: basePrice } = quotePrice({
                    orderPage,
                    details: {
                      duration: orderPage.duration,
                      rooms: 1,
                      persons: 1,
                      custom_details: {},
                      price: 0,
                    },
                    dateDetails: undefined,
                    account,
                  });
                  return (
                    <Grid
                      py={0}
                      item
                      key={`${orderPage.id}-${voucherType}`}
                      xs={12}
                      sm={voucherTiles.length <= 3 ? 12 : 6}
                      onClick={() => handleVoucherTileClick(orderPage, voucherType)}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          maxWidth: 400,
                          mx: 'auto',
                          display: 'flex',
                          flexDirection: 'column',
                          transition: 'transform 0.2s',
                          '&:hover': {
                            transform: 'scale(1.05)',
                          },
                          backgroundColor: theme.customTheme.palette.background,
                        }}>
                        {getPrimaryPhoto(orderPage) && (
                          <CardMedia
                            component="img"
                            sx={{
                              maxHeight: '300px',
                              height: isSmallScreen ? '70%' : '80%',
                              objectFit: 'cover',
                            }}
                            image={getPrimaryPhoto(orderPage, isSmallScreen ? 400 : 500)}
                            alt={orderPage.label[language]}
                          />
                        )}
                        <CardContent
                          sx={{
                            flexGrow: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            backgroundColor: theme.customTheme.palette.background,
                          }}>
                          <OrderPageTypography variant="h6" bold>
                            {isMonetary ? t('multiplVouchers.giftCardTitle') : orderPage.label[language]}
                          </OrderPageTypography>
                          <OrderPageTypography variant="body1">
                            {isMonetary
                              ? orderPage.label[language]
                              : t('multiplVouchers.startingFrom', {
                                  price: customerFacingPriceFormatter(
                                    withBookingFee(basePrice, account),
                                    account!,
                                    true,
                                  ),
                                })}
                          </OrderPageTypography>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <MonkeybookWidgetFooter languages={account?.customer_facing_widgets_languages} accountId={accountId} />
    </Grid>
  );
};
